import React, { useMemo } from 'react';
import Tag from 'Components/DesignSystem/Tag/Tag';
import DropdownButton from 'Components/DesignSystem/DropdownButton/DropdownButton';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Add } from 'Components/DesignSystem/Iconography/Icons';
import { ADAPTERS_BASE_ID, ADAPTERS_COPY } from './Constants';
import { useAdapters } from './Context/AdaptersContext';
import { availablePartiesForAdapter } from './assignmentState';
import { AdapterRow } from './types';

/**
 * The Parties column of one adapter row: read-only chips in view mode; removable chips plus an
 * "Add party" menu in edit mode. The menu lists only parties not yet assigned to ANY adapter of
 * this row's feed type — the per-feed exclusivity rule — and is disabled when nothing is left.
 */
const PartiesCell = ({ adapter }: { adapter: AdapterRow }) => {
  const { current, adapters, parties, isEditMode, partyName, addParty, removeParty } = useAdapters();
  const rowId = `${ADAPTERS_BASE_ID}-row-${adapter.key}`;
  const assigned = current[adapter.key] ?? [];
  const available = useMemo(
    () => availablePartiesForAdapter(current, adapters, parties, adapter.key),
    [current, adapters, parties, adapter.key],
  );

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5" data-testid={`${rowId}-parties`}>
      {assigned.length === 0 && (
        <Typography
          variant="sub-body-supporting"
          as="span"
          className="tw-text-ink-secondary"
          data-testid={`${rowId}-no-parties`}
        >
          {ADAPTERS_COPY.noParties}
        </Typography>
      )}
      {assigned.map((partyId) => {
        const name = partyName(partyId);
        return (
          <Tag
            key={partyId}
            label={name}
            title={name}
            variant="meta"
            data-testid={`${rowId}-party-${partyId}`}
            onRemove={isEditMode ? () => removeParty(adapter.key, partyId) : undefined}
            removeLabel={`Remove ${name} from ${adapter.name}`}
          />
        );
      })}
      {isEditMode && (
        <DropdownButton
          label={ADAPTERS_COPY.addParty}
          icon={<Add size={16} />}
          variant="subtle"
          size="sm"
          menuVariant="compact"
          menuMaxHeight="18rem"
          align="left"
          options={available.map((party) => ({ id: party.id, label: party.name }))}
          disabled={available.length === 0}
          onSelect={(optionId) => addParty(adapter.key, String(optionId))}
          data-testid={`${rowId}-add-party`}
        />
      )}
    </div>
  );
};

export default PartiesCell;
